import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { trackFormSubmit, identifyUser } from "@/utils/tracking";
import { Progress } from "@/components/ui/progress";
import { ImageUploader } from "@/components/forms/ImageUploader";
import { Combobox } from "@/components/ui/combobox";
import { storeUTMParams, useUTMParams } from "@/hooks/useUTMParams";
import { PhoneVerification } from "@/components/admin/PhoneVerification";

import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import vinInfoImage from "@/assets/vin-info.png";

// Data Lists
const years = Array.from({ length: 2025 - 1986 + 1 }, (_, i) => 2025 - i);
const yearOptions = years.map(y => ({ label: y.toString(), value: y.toString() }));

const makes = [
    "Acura", "Alfa Romeo", "Audi", "BMW", "Buick", "Cadillac", "Chevrolet", "Chrysler",
    "Dodge", "Ford", "GMC", "Genesis", "Honda", "Hyundai", "INEOS", "Infiniti", "Jaguar",
    "Jeep", "Kia", "Land Rover", "Lexus", "Lincoln", "Lucid", "Maserati", "Mazda",
    "Mercedes-Benz", "Mini", "Mitsubishi", "Nissan", "Polestar", "Porsche", "Ram",
    "Rivian", "Subaru", "Tesla", "Toyota", "VinFast", "Volkswagen", "Volvo"
];
const makeOptions = makes.map(m => ({ label: m, value: m }));

const provinces = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YK"];

const colors = [
    "Black", "Gray", "Silver", "White", "Blue",
    "Green", "Red", "Brown", "Gold", "Beige", "Other"
];

const sellingReasons = [
    "Looking to get cash for my car",
    "Looking to upgrade",
    "Downsizing",
    "Moving",
    "Vehicle no longer needed",
    "Other"
];
const sellingReasonOptions = sellingReasons.map(r => ({ label: r, value: r }));

interface SellFormData {
    year: string;
    make: string;
    model: string;
    trim: string;
    vin: string; // Added VIN
    city: string;
    province: string;
    odometer: string;
    exteriorColor: string;
    interiorColor: string;
    transmission: "Automatic" | "Manual" | "";
    exteriorDamage: string; // "true" or "false"
    interiorDamage: string;
    accidentClaims: string;
    smokedIn: string;
    windshieldCrack: string;
    keys: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    images: string[];
    customExteriorColor: string; // Internal use
    customInteriorColor: string; // Internal use
    reasonForSelling: string;
    contactPreference: 'email' | 'phone';
}

const steps = [
    { id: 1, title: "Vehicle Details" },
    { id: 2, title: "Specs & Condition" },
    { id: 3, title: "History" },
    { id: 4, title: "Photos" },
    { id: 5, title: "Personal Info" },
    { id: 6, title: "Contact" },
];

export default function SellYourCar() {
    const utm = useUTMParams();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastSubmitTime, setLastSubmitTime] = useState(0);
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const [formData, setFormData] = useState<SellFormData>({
        year: "", make: "", model: "", trim: "", vin: "", city: "", province: "",
        odometer: "", exteriorColor: "", interiorColor: "", transmission: "",
        exteriorDamage: "", interiorDamage: "", accidentClaims: "", smokedIn: "",
        windshieldCrack: "", keys: "",
        firstName: "", lastName: "", email: "", phone: "",
        images: [], customExteriorColor: "", customInteriorColor: "", reasonForSelling: "",
        contactPreference: 'phone'
    });

    const updateField = (field: keyof SellFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, 6));
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const validateStep = (step: number) => {
        switch (step) {
            case 1:
                if (!formData.year || !formData.make || !formData.model || !formData.trim || !formData.city || !formData.province) {
                    toast.error("Please fill in all vehicle details.");
                    return false;
                }
                if (formData.vin) {
                    // Basic check: 17 chars, no I, O, Q
                    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
                    if (!vinRegex.test(formData.vin)) {
                        toast.error("VIN must be 17 characters and cannot contain I, O, or Q.");
                        return false;
                    }
                }
                return true;
            case 2:
                if (!formData.odometer || !formData.exteriorColor || !formData.interiorColor || !formData.transmission) {
                    toast.error("Please complete the vehicle specs.");
                    return false;
                }
                return true;
            case 3:
                if (!formData.exteriorDamage || !formData.interiorDamage || !formData.accidentClaims || !formData.smokedIn || !formData.windshieldCrack || !formData.keys) {
                    toast.error("Please answer all condition questions.");
                    return false;
                }
                return true;
            case 4: // Photos & Reason
                if (!formData.reasonForSelling) {
                    toast.error("Please provide a reason for selling.");
                    return false;
                }
                return true;
            case 5: // Name
                if (!formData.firstName || !formData.lastName) {
                    toast.error("Please enter your name.");
                    return false;
                }
                return true;
            case 6: // Contact - Handled by submit
                return true;
            default: return true;
        }
    };

    const handleSubmit = async () => {
        if (!formData.email || !formData.phone) {
            toast.error("Contact info is missing.");
            return;
        }

        if (!isPhoneVerified) {
            toast.error("Please verify your phone number first.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }

        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(formData.phone.replace(/\D/g, ""))) {
            toast.error("Phone number must be exactly 10 digits.");
            return;
        }

        // Rate limit: 60 seconds between submissions
        const now = Date.now();
        if (now - lastSubmitTime < 60_000) {
            toast.error("Please wait a moment before submitting again.");
            return;
        }

        setIsSubmitting(true);
        try {
            const { error } = await supabase.rpc("submit_contact_form", {
                p_name: `${formData.firstName} ${formData.lastName}`,
                p_email: formData.email,
                p_phone: formData.phone,
                p_message: `Sell Your Car Request: ${formData.year} ${formData.make} ${formData.model} ${formData.trim}`,
                p_notes: JSON.stringify({
                    ...formData,
                    exteriorColor: formData.exteriorColor === "Other" ? formData.customExteriorColor : formData.exteriorColor,
                    interiorColor: formData.interiorColor === "Other" ? formData.customInteriorColor : formData.interiorColor,
                }),
                p_contact_preference: formData.contactPreference,
            });

            if (error) throw error;

            // Track conversion events (Meta Pixel Lead + Google Ads)
            trackFormSubmit('sell-your-car');
            identifyUser(formData.email, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                vehicle: `${formData.year} ${formData.make} ${formData.model}`,
            });

            storeUTMParams(utm);

            // Admin will handle CRM sync manually in Leads Management dashboard

            // Send email notification
            try {
                await supabase.functions.invoke("send-email", {
                    body: {
                        type: "sell-request",
                        data: formData,
                    },
                });
            } catch (emailError) {
                console.error("Failed to send email notification", emailError);
            }

            toast.success("Your offer request has been sent!");
            // Redirect or show success state
            setCurrentStep(7); // Virtual success step
        } catch (e) {
            console.error(e);
            toast.error("Failed to submit. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Header />
            <SEO title="Sell Your Car for Cash or Trade-In Value" description="Want to sell your car in Ontario? Get a top-dollar cash offer or trade-in value at Car Street. Fast, fair appraisals with no obligation." url="https://carstreet.ca/sell-your-car" />

            <main className="flex-1 py-12 px-4">
                <div className="max-w-xl mx-auto">
                    {currentStep <= 6 && (
                        <div className="mb-6 space-y-2">
                            <div className="flex justify-between text-sm text-muted-foreground">
                                <span>Steps to complete: {currentStep} / 6</span>
                                <span>{Math.round((currentStep / 6) * 100)}%</span>
                            </div>
                            <Progress value={(currentStep / 6) * 100} className="h-2" />
                        </div>
                    )}

                    <div className="bg-card border rounded-xl p-6 shadow-sm">
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <h2 className="text-2xl font-bold text-center">Vehicle Details</h2>
                                <p className="text-center text-muted-foreground">Enter vehicle details to get an offer.</p>

                                <div className="space-y-2">
                                    <Label>Vehicle Identification Number (VIN)</Label>
                                    <Input
                                        placeholder="Enter your VIN"
                                        value={formData.vin}
                                        onChange={e => updateField("vin", e.target.value.toUpperCase())}
                                        maxLength={17}
                                    />
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button variant="link" className="px-0 h-auto text-xs text-blue-600 underline">
                                                Where can I find my VIN?
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-md">
                                            <img src={vinInfoImage} alt="VIN Location Guide" className="w-full rounded-md" />
                                        </DialogContent>
                                    </Dialog>
                                </div>

                                <div className="border-t my-4"></div>

                                <Combobox
                                    options={yearOptions}
                                    value={formData.year}
                                    onValueChange={(v) => updateField("year", v)}
                                    placeholder="Select Year"
                                />

                                <Combobox
                                    options={makeOptions}
                                    value={formData.make}
                                    onValueChange={(v) => updateField("make", v)}
                                    placeholder="Select Make"
                                />

                                <Input placeholder="Model (e.g. Civic, F-150)" value={formData.model} onChange={e => updateField("model", e.target.value)} />
                                <Input placeholder="Trim (e.g. Touring, Lariat)" value={formData.trim} onChange={e => updateField("trim", e.target.value)} />

                                <div className="grid grid-cols-2 gap-2">
                                    <Input placeholder="City" value={formData.city} onChange={e => updateField("city", e.target.value)} />
                                    <Select value={formData.province} onValueChange={(v) => updateField("province", v)}>
                                        <SelectTrigger><SelectValue placeholder="Prov" /></SelectTrigger>
                                        <SelectContent>{provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>

                                <Button onClick={nextStep} className="w-full mt-4">Get My Offer</Button>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold">{formData.year} {formData.make} Details</h2>
                                <Input placeholder="Odometer Reading (KM)" type="number" value={formData.odometer} onChange={e => updateField("odometer", e.target.value)} />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Select value={formData.exteriorColor} onValueChange={v => updateField("exteriorColor", v)}>
                                            <SelectTrigger><SelectValue placeholder="Exterior Colour" /></SelectTrigger>
                                            <SelectContent>{colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select>
                                        {formData.exteriorColor === "Other" && (
                                            <Input placeholder="Enter Exterior Colour" value={formData.customExteriorColor} onChange={e => updateField("customExteriorColor", e.target.value)} />
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Select value={formData.interiorColor} onValueChange={v => updateField("interiorColor", v)}>
                                            <SelectTrigger><SelectValue placeholder="Interior Colour" /></SelectTrigger>
                                            <SelectContent>{colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select>
                                        {formData.interiorColor === "Other" && (
                                            <Input placeholder="Enter Interior Colour" value={formData.customInteriorColor} onChange={e => updateField("customInteriorColor", e.target.value)} />
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Transmission</Label>
                                    <div className="flex gap-2">
                                        {["Automatic", "Manual"].map(t => (
                                            <Button
                                                key={t}
                                                variant={formData.transmission === t ? "default" : "outline"}
                                                onClick={() => updateField("transmission", t)}
                                                className="flex-1"
                                            >
                                                {t}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button variant="ghost" onClick={prevStep}>Back</Button>
                                    <Button onClick={nextStep}>Continue</Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 3 && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-bold">History & Condition</h2>

                                {[
                                    { k: "exteriorDamage", q: "Any existing exterior damage?" },
                                    { k: "interiorDamage", q: "Any existing interior damage?" },
                                    { k: "accidentClaims", q: "Any accidents or claims?" },
                                    { k: "smokedIn", q: "Has it been smoked in?" },
                                    { k: "windshieldCrack", q: "Any windshield chips/cracks?" }
                                ].map(item => (
                                    <div key={item.k} className="space-y-2">
                                        <Label>{item.q}</Label>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant={formData[item.k as keyof SellFormData] === "true" ? "default" : "outline"}
                                                onClick={() => updateField(item.k as keyof SellFormData, "true")}
                                                className="flex-1"
                                            >
                                                Yes
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={formData[item.k as keyof SellFormData] === "false" ? "default" : "outline"}
                                                onClick={() => updateField(item.k as keyof SellFormData, "false")}
                                                className="flex-1"
                                            >
                                                No
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                <div className="space-y-2">
                                    <Label>How many key fobs?</Label>
                                    <div className="flex gap-1">
                                        {["None", "1 Key", "2+ Keys"].map(k => (
                                            <Button
                                                key={k}
                                                size="sm"
                                                variant={formData.keys === k ? "default" : "outline"}
                                                onClick={() => updateField("keys", k)}
                                                className="flex-1 text-xs"
                                            >
                                                {k}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button variant="ghost" onClick={prevStep}>Back</Button>
                                    <Button onClick={nextStep}>Continue</Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 4 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold">Photos (Optional)</h2>
                                <p className="text-sm text-muted-foreground">Upload photos to get a more accurate offer.</p>

                                <ImageUploader
                                    existingImages={formData.images}
                                    onImagesChange={(urls) => updateField("images", urls)}
                                />

                                <div className="space-y-4 pt-4 border-t mt-4">
                                    <h3 className="font-semibold">Additional Details</h3>
                                    <div className="space-y-2">
                                        <Label>What is the reason for selling?</Label>
                                        <Combobox
                                            options={sellingReasonOptions}
                                            value={formData.reasonForSelling}
                                            onValueChange={(v) => updateField("reasonForSelling", v)}
                                            placeholder="Select a reason"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button variant="ghost" onClick={prevStep}>Back</Button>
                                    <Button onClick={nextStep}>Continue</Button>
                                </div>
                            </div>
                        )}
                        {currentStep === 5 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold">What is your name?</h2>
                                <Input placeholder="First Name" value={formData.firstName} onChange={e => updateField("firstName", e.target.value)} />
                                <Input placeholder="Last Name" value={formData.lastName} onChange={e => updateField("lastName", e.target.value)} />

                                <div className="flex justify-between pt-4">
                                    <Button variant="ghost" onClick={prevStep}>Back</Button>
                                    <Button onClick={nextStep}>Continue</Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 6 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold">Your Offer Is Ready!</h2>
                                <p className="text-sm text-muted-foreground text-center">Please share your contact information so we can provide you with your offer.</p>

                                {/* Contact Preference */}
                                <div className="space-y-2">
                                    <span className="text-sm font-medium">How would you prefer we contact you? <span className="text-red-500">*</span></span>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => updateField("contactPreference", 'phone')}
                                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                                                formData.contactPreference === 'phone'
                                                    ? 'border-primary bg-primary/5 text-primary'
                                                    : 'border-border hover:border-primary/50'
                                            }`}
                                        >
                                            <span className="text-2xl">📱</span>
                                            <span className="font-semibold text-sm">Phone / Text</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateField("contactPreference", 'email')}
                                            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                                                formData.contactPreference === 'email'
                                                    ? 'border-primary bg-primary/5 text-primary'
                                                    : 'border-border hover:border-primary/50'
                                            }`}
                                        >
                                            <span className="text-2xl">📧</span>
                                            <span className="font-semibold text-sm">Email</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <span className="text-sm font-medium">Email Address <span className="text-red-500">*</span></span>
                                    <Input placeholder="you@example.com" type="email" value={formData.email} onChange={e => updateField("email", e.target.value)} />
                                </div>
                                
                                <PhoneVerification
                                    phone={formData.phone}
                                    onPhoneChange={(p) => updateField("phone", p)}
                                    onVerified={setIsPhoneVerified}
                                    isVerified={isPhoneVerified}
                                />

                                <p className="text-xs text-center text-muted-foreground">By clicking Submit, you agree to our Terms of Use.</p>

                                <div className="flex justify-between pt-4">
                                    <Button variant="ghost" onClick={prevStep}>Back</Button>
                                    <Button onClick={handleSubmit} disabled={isSubmitting || !isPhoneVerified || !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}>
                                        {isSubmitting ? "Sending..." : "Submit"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 7 && (
                            <div className="text-center space-y-4 py-8">
                                <div className="text-5xl">🎉</div>
                                <h2 className="text-2xl font-bold">Request Received!</h2>
                                <p className="text-muted-foreground">We are reviewing your vehicle details and will contact you shortly with an offer.</p>
                                <Button onClick={() => window.location.href = "/"}>Back to Home</Button>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
